import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Avatar,
  IconButton,
  Alert,
  useTheme,
  SelectChangeEvent
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  Print as PrintIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import api from '../utils/api';
import { muiStyles } from './CheckIn.styles';
import styles from './CheckIn.module.css';

// Add Node.js process type
declare const process: {
  env: {
    NODE_ENV: 'development' | 'production' | 'test';
  };
};

// Types
interface Host {
  id: number;
  name: string;
  department: string;
  email: string;
  role?: string;
}

interface VisitorFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  purpose: string;
  host_id: number | '';
  notes: string;
}

interface VisitorResponse {
  id: number;
  name: string;
  badge_id: string;
  host_name: string;
  purpose: string;
  check_in_time: string;
}

// Form validation
interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  purpose?: string;
  host_id?: string;
}

const validateForm = (data: VisitorFormData): FormErrors => {
  const errors: FormErrors = {};
  
  if (!data.name.trim()) {
    errors.name = 'Name is required';
  }
  
  if (!data.phone.trim()) {
    errors.phone = 'Phone number is required';
  }
  
  if (!data.purpose.trim()) {
    errors.purpose = 'Purpose is required';
  }
  
  if (data.host_id === '') {
    errors.host_id = 'Please select a host';
  }
  
  return errors;
};

const CheckIn: React.FC = () => {
  // State for form data
  const [formData, setFormData] = useState<VisitorFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    purpose: '',
    host_id: '',
    notes: ''
  });
  
  // State for hosts
  const [hosts, setHosts] = useState<Host[]>([]);
  const [loadingHosts, setLoadingHosts] = useState(true);
  
  // State for webcam
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  
  // State for form submission
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checkedInVisitor, setCheckedInVisitor] = useState<VisitorResponse | null>(null);
  
  // Refs
  const webcamRef = useRef<Webcam>(null);
  
  // Hooks
  const theme = useTheme();
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Steps for the check-in process
  const steps = ['Visitor Information', 'Take Photo', 'Confirmation'];
  
  // Purpose options
  const purposeOptions = [
    'Meeting',
    'Interview',
    'Delivery',
    'Presentation',
    'Client Meeting',
    'Service',
    'Training',
    'Conference',
    'Inspection',
    'Tour',
    'Other'
  ];
  
  // Check if there's a visitor ID in the URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const visitorId = params.get('visitor');
    
    if (visitorId) {
      // Fetch visitor data
      const fetchVisitor = async () => {
        try {
          const response = await api.get(`/api/visitors/${visitorId}`);
          const visitor = response.data;
          
          setFormData({
            name: visitor.name,
            email: visitor.email || '',
            phone: visitor.phone || '',
            company: visitor.company || '',
            purpose: visitor.purpose || '',
            host_id: visitor.host_id || '',
            notes: visitor.notes || ''
          });
        } catch (err) {
          console.error('Error fetching visitor data:', err);
        }
      };
      
      fetchVisitor();
    }
  }, [location.search]);
  
  // Fetch hosts from API
  useEffect(() => {
    const fetchHosts = async () => {
      try {
        setLoadingHosts(true);
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await api.get(`/api/users/hosts?t=${timestamp}`);
        console.log('Hosts data received:', response.data);
        console.log('Number of hosts:', response.data.length);
        setHosts(response.data);
        setLoadingHosts(false);
      } catch (err) {
        console.error('Error fetching hosts:', err);
        setLoadingHosts(false);
      }
    };
    
    fetchHosts();
  }, []);
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name as string]: undefined
      }));
    }
  };
  
  // Handle select change
  const handleSelectChange = (event: SelectChangeEvent<string | number>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle camera toggle
  const handleToggleCamera = () => {
    setCameraEnabled(prev => !prev);
    setPhotoTaken(false);
    setPhotoData(null);
  };
  
  // Handle taking photo
  const handleTakePhoto = () => {
    if (webcamRef.current) {
      const screenshot = webcamRef.current.getScreenshot();
      setPhotoData(screenshot);
      setPhotoTaken(true);
    }
  };
  
  // Handle retaking photo
  const handleRetakePhoto = () => {
    setPhotoTaken(false);
    setPhotoData(null);
  };
  
  // Handle next step
  const handleNext = () => {
    if (activeStep === 0) {
      // Validate form data
      const validationErrors = validateForm(formData);
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }
    
    if (activeStep === 1 && !photoData && !photoTaken) {
      // Skip photo step if camera is not enabled
      if (!cameraEnabled) {
        setActiveStep(prev => prev + 1);
        return;
      }
      
      // Show error if no photo is taken
      alert('Please take a photo or skip this step');
      return;
    }
    
    setActiveStep(prev => prev + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // First create or get visitor
      let visitorId;
      const visitorParams = new URLSearchParams(location.search);
      const preRegisteredId = visitorParams.get('visitor');
      
      if (preRegisteredId) {
        // Use existing visitor
        visitorId = preRegisteredId;
      } else {
        // Create new visitor
        const createResponse = await api.post('/api/visitors', {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          purpose: formData.purpose,
          host_id: formData.host_id,
          notes: formData.notes
        });
        
        visitorId = createResponse.data.id;
      }
      
      // Then check in the visitor
      const checkInResponse = await api.post(`/api/visitors/${visitorId}/check-in`, {
        photo: photoData // Send the full data URL from webcam
      });
      
      // Set the checked-in visitor and update UI
      setCheckedInVisitor(checkInResponse.data);
      setIsSubmitting(false);
    } catch (err: any) {
      console.error('Error checking in visitor:', err);
      setSubmitError(err.response?.data?.message || 'Failed to check in visitor. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  // Handle print badge
  const handlePrintBadge = () => {
    if (!checkedInVisitor) return;
    
    // In a real application, this would trigger badge printing
    // For demo purposes, we'll just open a new window with badge info
    const badgeWindow = window.open('', '_blank');
    
    if (badgeWindow) {
      badgeWindow.document.write(`
        <html>
          <head>
            <title>Visitor Badge</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
              }
              .badge {
                border: 1px solid #ccc;
                border-radius: 8px;
                padding: 20px;
                width: 300px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              }
              .badge-header {
                text-align: center;
                margin-bottom: 20px;
              }
              .badge-content {
                margin-bottom: 20px;
              }
              .badge-footer {
                text-align: center;
                font-size: 12px;
                color: #666;
              }
              .badge-id {
                font-size: 18px;
                font-weight: bold;
                text-align: center;
                margin: 10px 0;
                color: #1976d2;
              }
              .field {
                margin-bottom: 10px;
              }
              .label {
                font-weight: bold;
                color: #666;
              }
            </style>
          </head>
          <body>
            <div class="badge">
              <div class="badge-header">
                <h2>VISITOR</h2>
              </div>
              <div class="badge-id">${checkedInVisitor.badge_id}</div>
              <div class="badge-content">
                <div class="field">
                  <div class="label">Name:</div>
                  <div>${checkedInVisitor.name}</div>
                </div>
                <div class="field">
                  <div class="label">Host:</div>
                  <div>${checkedInVisitor.host_name}</div>
                </div>
                <div class="field">
                  <div class="label">Purpose:</div>
                  <div>${checkedInVisitor.purpose}</div>
                </div>
                <div class="field">
                  <div class="label">Date:</div>
                  <div>${new Date(checkedInVisitor.check_in_time).toLocaleDateString()}</div>
                </div>
              </div>
              <div class="badge-footer">
                Please wear this badge visibly at all times while on the premises.
              </div>
            </div>
            <script>
              window.onload = function() {
                window.print();
              }
            </script>
          </body>
        </html>
      `);
      badgeWindow.document.close();
    }
  };
  
  // Render form step
  const renderFormStep = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            error={!!errors.phone}
            helperText={errors.phone}
            margin="normal"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Company"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal" error={!!errors.purpose} required>
            <InputLabel id="purpose-label">Purpose</InputLabel>
            <Select
              labelId="purpose-label"
              id="purpose"
              name="purpose"
              value={formData.purpose}
              label="Purpose"
              onChange={handleSelectChange}
            >
              {purposeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {errors.purpose && <FormHelperText>{errors.purpose}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal" error={!!errors.host_id} required>
            <InputLabel id="host-label">Host</InputLabel>
            <Select
              labelId="host-label"
              id="host_id"
              name="host_id"
              value={formData.host_id}
              label="Host"
              onChange={handleSelectChange}
            >
              {hosts.length === 0 && (
                <MenuItem disabled value="">
                  <em>No hosts available</em>
                </MenuItem>
              )}
              {hosts.map(host => (
                <MenuItem key={host.id} value={host.id}>
                  {host.name} {host.role && `(${host.role})`}
                </MenuItem>
              ))}
            </Select>
            {errors.host_id && <FormHelperText>{errors.host_id}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            multiline
            rows={1}
            maxRows={1}
            value={formData.notes}
            onChange={handleInputChange}
            margin="normal"
            sx={{ height: 36, fontSize: 14, resize: 'none' }}
          />
        </Grid>
      </Grid>
    );
  };
  
  // Render photo step
  const renderPhotoStep = () => {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleToggleCamera}
            startIcon={<CameraIcon />}
            sx={{ mb: 2 }}
          >
            {cameraEnabled ? 'Disable Camera' : 'Enable Camera'}
          </Button>
        </Box>
        
        {cameraEnabled && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            width: '100%' 
          }}>
            {!photoTaken ? (
              <>
                <Box 
                  sx={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: 'auto',
                    mb: 2,
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                      facingMode: 'user',
                    }}
                    className={styles.webcam}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTakePhoto}
                  startIcon={<CameraIcon />}
                >
                  Take Photo
                </Button>
              </>
            ) : (
              <>
                <Box 
                  className={styles.photoBox}
                >
                  <img
                    src={photoData || ''}
                    alt="Visitor"
                    className={styles.photoTaken}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRetakePhoto}
                >
                  Retake Photo
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
    );
  };
  
  // Render confirmation step
  const renderConfirmationStep = () => {
    // Find selected host
    const selectedHost = hosts.find(host => host.id === formData.host_id);
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Confirm Visitor Information
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please review the visitor information before completing the check-in process.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Visitor Details
                </Typography>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                    Name:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formData.name}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                    Email:
                  </Typography>
                  <Typography variant="body2">
                    {formData.email || 'Not provided'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                    Phone:
                  </Typography>
                  <Typography variant="body2">
                    {formData.phone}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                    Company:
                  </Typography>
                  <Typography variant="body2">
                    {formData.company || 'Not provided'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                    Purpose:
                  </Typography>
                  <Typography variant="body2">
                    {formData.purpose}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Host Information
                </Typography>
                
                <Divider sx={{ mb: 2 }} />
                
                {selectedHost ? (
                  <>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                        Name:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedHost.name}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                        Department:
                      </Typography>
                      <Typography variant="body2">
                        {selectedHost.department}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                        Email:
                      </Typography>
                      <Typography variant="body2">
                        {selectedHost.email}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No host selected
                  </Typography>
                )}
              </CardContent>
            </Card>
            
            {photoData && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Visitor Photo
                  </Typography>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Avatar 
                      src={photoData} 
                      alt={formData.name}
                      sx={{ width: 120, height: 120 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  // Render success step
  const renderSuccessStep = () => {
    if (!checkedInVisitor) return null;
    
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Avatar 
          sx={{ 
            width: 80, 
            height: 80, 
            bgcolor: 'success.main', 
            mx: 'auto',
            mb: 2
          }}
        >
          <CheckIcon sx={{ fontSize: 40 }} />
        </Avatar>
        
        <Typography variant="h5" gutterBottom>
          Check-In Successful!
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          {checkedInVisitor.name} has been checked in successfully.
        </Typography>
        
        <Card sx={{ maxWidth: 400, mx: 'auto', mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Badge Information
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
              {checkedInVisitor.badge_id}
            </Typography>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                Visitor:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {checkedInVisitor.name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                Host:
              </Typography>
              <Typography variant="body2">
                {checkedInVisitor.host_name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                Purpose:
              </Typography>
              <Typography variant="body2">
                {checkedInVisitor.purpose}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrintBadge}
          sx={{ mr: 2 }}
        >
          Print Badge
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/visitors')}
        >
          View All Visitors
        </Button>
      </Box>
    );
  };
  
  // Render current step content
  const renderStepContent = () => {
    if (checkedInVisitor) {
      return renderSuccessStep();
    }
    
    switch (activeStep) {
      case 0:
        return renderFormStep();
      case 1:
        return renderPhotoStep();
      case 2:
        return renderConfirmationStep();
      default:
        return null;
    }
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {checkedInVisitor ? 'Check-In Complete' : 'Visitor Check-In'}
      </Typography>
      
      {!checkedInVisitor ? (
        <>
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel 
              sx={{ 
                mb: 3,
                '& .MuiStepLabel-label': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}>
              <Step>
                <StepLabel>Visitor Information</StepLabel>
              </Step>
              <Step>
                <StepLabel>Photo</StepLabel>
              </Step>
              <Step>
                <StepLabel>Confirmation</StepLabel>
              </Step>
            </Stepper>
            
            {renderStepContent()}
            
            {/* Navigation buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={activeStep === 0 ? () => navigate(-1) : handleBack}
              >
                {activeStep === 0 ? 'Cancel' : 'Back'}
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  Complete Check-In
                </Button>
              ) : (
                <Button
                  variant="contained"
                  endIcon={<ForwardIcon />}
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Paper>
        </>
      ) : (
        renderSuccessStep()
      )}
    </Box>
  );
};

export default CheckIn;
